import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { ROUTE_ACCOUNT_PRODUCTS } from '../../../../../config/routes';
import type { ProductType } from '../../helpers/digitalProductHelpers';

import './style.scss';

interface ProductAccessBlockedBannerProps {
  /** The type of product that is blocked. */
  productType: ProductType;
  /** Machine-readable reason code. */
  reason: string | null;
  /**
   * Visual variant of the banner.
   * - 'banner' (default): prominent info-style banner with icon, title, and actions.
   * - 'inline': compact error-style message placed inline within the form.
   */
  variant?: 'banner' | 'inline';
  /**
   * Callback to dismiss the banner (e-learning only).
   * Allows user to proceed with ordering for someone else.
   */
  onOrderForSomeoneElse?: () => void;
}

/**
 * Banner shown when the user already has access to a digital product.
 *
 * Displays a warning message and a link to the products page,
 * preventing the user from completing a duplicate purchase.
 *
 * For e-learning products, includes an option to order for someone else,
 * which dismisses the banner and shows the checkout form.
 *
 * @component
 */
const ProductAccessBlockedBanner: React.FC<ProductAccessBlockedBannerProps> = ({
  productType,
  reason,
  variant = 'banner',
  onOrderForSomeoneElse,
}) => {
  const { t } = useTranslation();

  const isElearning = productType === 'e_learning' || reason === 'USER_ALREADY_ENROLLED';

  const title = isElearning
    ? t('checkout.already_enrolled_title')
    : t('checkout.already_has_tool_title');

  const message = isElearning
    ? t('checkout.already_enrolled_message')
    : t('checkout.already_has_tool_message');

  if (variant === 'inline') {
    return (
      <div className="ProductAccessBlocked ProductAccessBlocked--inline" role="alert">
        <p className="ProductAccessBlocked__inline-message">{message}</p>
        <div className="ProductAccessBlocked__inline-actions">
          <Link
            to={ROUTE_ACCOUNT_PRODUCTS}
            className="ProductAccessBlocked__inline-link"
          >
            {t('checkout.go_to_products')}
          </Link>
          {isElearning && onOrderForSomeoneElse && (
            <button
              type="button"
              className="ProductAccessBlocked__inline-link"
              onClick={onOrderForSomeoneElse}
            >
              {t('checkout.order_for_someone_else')}
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="ProductAccessBlocked">
      <div className="ProductAccessBlocked__icon">
        <span className="icon icon-info" />
      </div>
      <div className="ProductAccessBlocked__content">
        <h3 className="ProductAccessBlocked__title">{title}</h3>
        <p className="ProductAccessBlocked__message">{message}</p>
        <div className="ProductAccessBlocked__actions">
          <Link
            to={ROUTE_ACCOUNT_PRODUCTS}
            className="Button Button--primary ProductAccessBlocked__button"
          >
            {t('checkout.go_to_products')}
            <span className="icon icon-arrow-right icon-variant-2" />
          </Link>
          {isElearning && onOrderForSomeoneElse && (
            <button
              type="button"
              className="ProductAccessBlocked__link"
              onClick={onOrderForSomeoneElse}
            >
              {t('checkout.order_for_someone_else')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductAccessBlockedBanner;
